import React from "react";

import { Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Input, Select } from "../../../../../../../../../components/Form";
import RadioButton from "../../../../../../../../../components/Form/RadioGroup";
import { Group } from "../../../../../../../../../components/Generic/ActualInventory/components/CreateActualItemModal/Checklist/styled";
import useTimezoneDate from "../../../../../../../../../hooks/useTimezoneDate";
import { Project } from "../../../../../../../../../models/project";
import { Status } from "../../../../../../../../../util/enums";
import { BaseFieldProps, ProjectDetailsFields, ProjectFields } from "../../types";
import { SectionTitleModal } from "./styles";

interface ProjectDetailsSectionProps {
  generateBaseFieldProps: (name: ProjectFields) => BaseFieldProps;
  handleChangeProject: (key: keyof Project, value: any) => void;
  project: Project | undefined;
}

export const ProjectDetails = ({
  generateBaseFieldProps,
  handleChangeProject,
  project,
}: ProjectDetailsSectionProps) => {
  const { t: translate } = useTranslation();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const isViewOrEdit = !!project?.id;

  return (
    <Grid container direction="row" alignItems="flex-start" spacing={2}>
      <Grid container item direction="row" alignItems="flex-start" spacing={2}>
        <Grid item xs={12} md={6} lg={6}>
          <Input
            required
            {...generateBaseFieldProps(ProjectDetailsFields.NAME)}
            onChange={(event) => handleChangeProject(ProjectDetailsFields.NAME, event.target.value)}
            label={translate("projectName")}
            placeholder={translate("projectName")}
            disabled={isViewOrEdit}
          />
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input
          type="date"
          required
          {...generateBaseFieldProps(ProjectDetailsFields.START_DATE)}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.START_DATE, event.target.value)
          }
          value={getDateValueUtcTimezone(project?.startDate)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input
          type="date"
          required
          {...generateBaseFieldProps(ProjectDetailsFields.END_DATE)}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.END_DATE, event.target.value)
          }
          value={getDateValueUtcTimezone(project?.endDate)}
        />
      </Grid>
      <Grid item xs={12}>
        <SectionTitleModal>{translate("emailConfiguration")}</SectionTitleModal>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          required
          {...generateBaseFieldProps(ProjectDetailsFields.EMAIL_HOST)}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.EMAIL_HOST, event.target.value)
          }
          label={translate("imapHost")}
          placeholder={translate("imapHost")}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input
          required
          {...generateBaseFieldProps(ProjectDetailsFields.EMAIL_PORT)}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.EMAIL_PORT, event.target.value)
          }
          label={translate("imapPort")}
          placeholder={translate("imapPort")}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          required
          {...generateBaseFieldProps(ProjectDetailsFields.EMAIL_USER)}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.EMAIL_USER, event.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          required
          {...generateBaseFieldProps(ProjectDetailsFields.EMAIL)}
          label={translate("emailAddress")}
          onChange={(event) => handleChangeProject(ProjectDetailsFields.EMAIL, event.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Input
          type="password"
          required
          {...generateBaseFieldProps(ProjectDetailsFields.EMAIL_PASSWORD)}
          inputProps={{
            autoComplete: "new-password",
          }}
          onChange={(event) =>
            handleChangeProject(ProjectDetailsFields.EMAIL_PASSWORD, event.target.value)
          }
        />
      </Grid>

      <Grid item>
        <Group>
          <RadioButton
            {...generateBaseFieldProps(ProjectDetailsFields.EMAIL_TLS)}
            onChange={(event) =>
              handleChangeProject(ProjectDetailsFields.EMAIL_TLS, Number(event.target.value))
            }
            value={project?.emailTls ?? 0}
            label={`${translate("useTls")}?`}
            options={[
              {
                label: translate("yes"),
                value: 1,
              },
              {
                label: translate("no"),
                value: 0,
              },
            ]}
          />
        </Group>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Select
          {...generateBaseFieldProps(ProjectDetailsFields.STATUS)}
          onChange={(event) => handleChangeProject(ProjectDetailsFields.STATUS, event.target.value)}
          displayEmpty
          value={project?.status ?? Status.ACTIVE}
        >
          {[Status.ACTIVE, Status.INACTIVE].map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Grid>
    </Grid>
  );
};
